import { ApolloQueryResult, FetchResult, gql } from '@apollo/client';
import { inject, injectable } from 'tsyringe';
import { UserDeleteResponse, UserInviteResponse, UserListResponse, UserUpdateResponse } from './interfaces/User.types';
import { AmGraphQlClient } from 'app/cross-cutting-concerns/communication/clients/AmGraphQlClient';
import {
  MutationUserDeleteByEmailArgs,
  MutationUserInviteArgs,
  MutationUserUpdateArgs,
  QueryUsersArgs,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';

export type UserClientListOptions = QueryUsersArgs & {
  skipApiCache?: boolean;
};
export type UserClientUpdateOptions = MutationUserUpdateArgs;
export type UserClientInviteOptions = MutationUserInviteArgs;
export type UserClientDeleteOptions = MutationUserDeleteByEmailArgs;

@injectable()
export class UserClient {
  constructor(@inject('AmGraphQlClient') private client: AmGraphQlClient) {}
  public list = async ({
    filter,
    paginationOptions,
    skipApiCache,
  }: UserClientListOptions): Promise<ApolloQueryResult<UserListResponse>> =>
    this.client.query({
      query: gql`
        query Users($filter: InputFilterUsersList, $paginationOptions: InputPagingOptions!) {
          users(filter: $filter, paginationOptions: $paginationOptions) {
            data {
              createdAt
              displayName
              id
              email
              role
              status
            }
            metadata {
              paginationToken
              totalCount
            }
          }
        }
      `,
      variables: {
        filter,
        paginationOptions,
      },
      ...(skipApiCache ? { context: { skipApiCache: true } } : {}),
    });

  public update = async ({ id, input }: UserClientUpdateOptions): Promise<FetchResult<UserUpdateResponse>> =>
    this.client.mutate({
      mutation: gql`
        mutation UserUpdate($id: ID!, $input: InputUserUpdate!) {
          userUpdate(id: $id, input: $input) {
            data {
              id
              displayName
              email
              role
              status
              createdAt
            }
          }
        }
      `,
      variables: {
        id,
        input,
      },
    });

  public invite = async ({ input }: UserClientInviteOptions): Promise<FetchResult<UserInviteResponse>> =>
    this.client.mutate({
      mutation: gql`
        mutation UserUpdate($input: InputUserInvite!) {
          userInvite(input: $input) {
            data
          }
        }
      `,
      variables: {
        input,
      },
    });

  public delete = async ({ input }: UserClientDeleteOptions): Promise<FetchResult<UserDeleteResponse>> =>
    this.client.mutate({
      mutation: gql`
        mutation UserDeleteByEmail($input: InputUserDeleteByEmail!) {
          userDeleteByEmail(input: $input) {
            data
          }
        }
      `,
      variables: {
        input,
      },
    });
}
